import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Demande {
  id: string;
  nom: string;
  prenom: string;
  telephone: string;
  email: string;
  service: string;
  demande: string;
  statut: string;
  region: string;
  ville: string;
  budget: string;
  urgence: string;
  date: string;
  is_free_lead?: boolean;
}

export default function LeadsQualifies() {
  const [demandes, setDemandes] = useState<Demande[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        setLoading(true);
      
        const { data, error } = await supabase
          .from('leads')
          .select('*')
          .in('statut', ['nouveau', 'en_cours'])
          .is('vendu_a', null)
          .order('created_at', { ascending: false })
          .limit(6);

        if (error) {
          console.error('Erreur Supabase:', error);
          
          // Message d'erreur spécifique pour la récursion RLS
          if (error.code === '42P17') {
            console.error('⚠️ ERREUR RLS: Récursion infinie détectée. Veuillez corriger les politiques RLS dans Supabase Dashboard.');
          }
          
          // Afficher un tableau vide au lieu de crasher
          setDemandes([]);
          setLoading(false);
          return;
        }

        console.log('Leads chargés:', data);
        setDemandes(data || []);
      } catch (error: any) {
        console.error('Erreur lors du chargement des leads:', error);
        setDemandes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, []);

  const getZoneInfo = (zone: string) => {
    switch (zone) {
      case 'A':
        return {
          name: 'Zone A - Toulouse Centre',
          color: 'bg-emerald-500',
          textColor: 'text-emerald-600'
        };
      case 'B':
        return {
          name: 'Zone B - Périphérie',
          color: 'bg-blue-500',
          textColor: 'text-blue-600'
        };
      case 'C':
        return {
          name: 'Zone C - Haute-Garonne',
          color: 'bg-purple-500',
          textColor: 'text-purple-600'
        };
      default:
        return {
          name: zone,
          color: 'bg-gray-500',
          textColor: 'text-gray-600'
        };
    }
  };

  const getServiceIcon = (service: string) => {
    const serviceMap: { [key: string]: string } = {
      'Fenêtres': 'ri-window-line',
      'Portes': 'ri-door-open-line',
      'Volets': 'ri-layout-grid-line',
      'Portails': 'ri-gate-line',
      'Vérandas': 'ri-home-4-line',
      'Pergolas': 'ri-building-line'
    };
    return serviceMap[service] || 'ri-tools-line';
  };

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Leads Qualifiés Disponibles
            </h2>
            <p className="text-xl text-gray-600">
              Chargement des demandes en cours...
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (demandes.length === 0) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Leads Qualifiés Disponibles
            </h2>
            <p className="text-xl text-gray-600">
              Aucun lead disponible pour le moment
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Leads Qualifiés Disponibles
          </h2>
          <p className="text-xl text-gray-600">
            Des demandes vérifiées et prêtes à être traitées
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {demandes.map((demande) => {
            const zoneInfo = getZoneInfo(demande.region);
            return (
              <div
                key={demande.id}
                className="bg-white rounded-xl border-2 border-gray-100 p-6 hover:border-emerald-500 hover:shadow-xl transition-all duration-300 cursor-pointer relative overflow-hidden"
              >
                {demande.is_free_lead && (
                  <div className="absolute top-0 right-0 bg-gradient-to-br from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold rounded-bl-xl">
                    GRATUIT
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <i className={`${getServiceIcon(demande.service)} text-2xl text-emerald-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{demande.service}</h3>
                      <p className="text-sm text-gray-500">{demande.ville}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 ${zoneInfo.color} text-white text-xs font-semibold rounded-full whitespace-nowrap`}>
                    Zone {demande.region}
                  </span>
                </div>

                <p className="text-gray-600 mb-4 line-clamp-2">
                  {demande.demande}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-money-euro-circle-line text-emerald-600"></i>
                    <span className="text-gray-700">Budget: <strong>{demande.budget}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-time-line text-emerald-600"></i>
                    <span className="text-gray-700">Urgence: <strong>{demande.urgence}</strong></span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <i className="ri-calendar-line text-emerald-600"></i>
                    <span className="text-gray-700">{demande.date}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg transition-colors whitespace-nowrap">
                    Voir les détails
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <a
            href="/membre"
            className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold px-8 py-4 rounded-lg transition-colors whitespace-nowrap"
          >
            Voir tous les leads disponibles
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
